import classNames from 'classnames';
import {isNil} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {WithRouterProps} from 'react-router';

import Assignment from './Assignment';
import AssignmentStatusDropdown, {Value as DropdownValue} from './AssignmentStatusDropdown';
import {LoadingSpinner} from '@albert-io/atomic';
import TabEmptyState from '../TabEmptyState';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';
import useStudentAssignments from './hooks/useStudentAssignments';

export default function StudentHomepageAssignmentsTabContent({params}: WithRouterProps) {
  const areClassroomsLoading = useStudentDashboard((state) => state.areClassroomsLoading);

  const classroomId: string | undefined = params.classroomId;

  const paginationTrigger = useRef<HTMLDivElement | null>(null);

  const [dropdownValue, setDropdownValue] = useState<DropdownValue>('toDo');

  // This query will always get open assignments regardless of the current dropdown state in order to
  // provide an accurate empty state for "To-Do" assignments
  const {assignments: openAssignments, loading: loadingAll} = useStudentAssignments(
    classroomId,
    'open'
  );

  const {assignments, fetchMore, isLoadingMore, loading, resetMoreAssignments} =
    useStudentAssignments(classroomId, dropdownValue);

  function onDropdownValueChange(newDropdownValue: DropdownValue) {
    setDropdownValue(newDropdownValue);
    resetMoreAssignments();
  }

  const isLoading = areClassroomsLoading || loading || loadingAll;

  useEffect(() => {
    const localPaginationTrigger = paginationTrigger?.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (dropdownValue !== 'toDo' && entries[0].intersectionRatio === 1) {
          fetchMore();
        }
      },
      {
        root: document.querySelector('#tabContentContainer'),
        threshold: 1
      }
    );

    if (!isNil(localPaginationTrigger)) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
      observer.observe(localPaginationTrigger);
    }

    return () => {
      if (!isNil(localPaginationTrigger)) {
        observer.unobserve(localPaginationTrigger);
        observer.disconnect();
      }
    };
  }, [dropdownValue, fetchMore]);

  return (
    <>
      <div className='relative flex flex-col gap-x2 overflow-hidden px-x1 py-x3 m:px-x4'>
        <AssignmentStatusDropdown
          className='mb-x1'
          disabled={isLoading}
          isScopedToClassroom={classroomId !== undefined}
          onValueChange={onDropdownValueChange}
          value={dropdownValue}
        />
        {isLoading && <LoadingSpinner bottomMarginLevel={0} size={3} />}
        {!isLoading && assignments.length === 0 && (
          <TabEmptyState
            copy={
              dropdownValue === 'toDo' && openAssignments.length > 0
                ? 'No upcoming assignments'
                : 'Your assignments will appear here'
            }
            icon={['fal', 'file-alt']}
          />
        )}
        {!isLoading &&
          assignments.length > 0 &&
          assignments.map((assignment) => (
            <Assignment assignment={assignment} key={assignment.assignmentId} />
          ))}
        {!isLoading && (
          <div
            className={classNames(
              'absolute flex w-full justify-center transition-[bottom] duration-200',
              {
                'bottom-x3': isLoadingMore,
                '-bottom-x7': !isLoadingMore
              }
            )}
          >
            <div className='flex h-x7 w-x7 items-center rounded-full background-subtle p-x1 elevation-shadow-l2'>
              <LoadingSpinner bottomMarginLevel={0} size={2} />
            </div>
          </div>
        )}
      </div>
      <div ref={paginationTrigger} />
    </>
  );
}
